import { MouseEventHandler, useEffect, useRef, useState } from 'react';
import qs from 'qs';
import { useNavigate, useLocation, useParams, NavigateFunction } from 'react-router-dom';
import {
    getAppointment,
    getDMSAppointment,
    IAppointment,
    IService,
    IXtimeServiceBase,
    MaintenanceMenu,
    RNDService,
    ServiceComment,
    XTimeServiceProperties,
} from '../../../API/GoMoto';
import { ServiceCategory, AccountType } from '../../../API/Enums';
import {
    AppointmentDispatchTypes,
    useAppointmentDispatch,
    useAppointmentUpdateAndPersist,
    useEnsureAppointment,
    useMaskPhone,
    useMaskEmail,
} from '../../../context/Appointment/appointment';
import { Types, useCartDispatch } from '../../../context/cart';
import { BackgroundDispatchTypes, useBackgroundDataDispatch } from '../../../context/backgroundData';
import { useAdditionalDealers, useKioskTimeTable, useKioskState, useSwitchDealer } from '../../../context/Kiosk/kiosk';
import { useLocalization, useLocalizationDispatch } from '../../../context/Localization/localization';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { CTAButton } from '../../../controls/ButtonBase';
import { DataBox, DataBoxItem } from '../../../controls/DataBox/DataBox';
import { Heading } from '../../../controls/Heading/Heading';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { isMobileCheckInResumptionExpired } from '../../../helpers/Appointment';
import { formatAsPhoneNumber } from '../../../helpers/Number';
import { ScrollableBody } from '../../Layout/ScrollableBody/ScrollableBody';
import { textCapitalize } from '../../../helpers/textCapitalize';
import { useDisplayLocalizedVehicle } from '../../../helpers/displayVehicle';
import dayjs from 'dayjs';
import { AppRoutePaths, AppointmentRoutePaths, Appointment_KeydropRoutePaths, PathName } from '../../../helpers/Routes';
import { GMDialogError } from '../../../controls/Dialog';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { LocalizationDispatchTypes, LocalizationType } from '../../../context/Localization/localization.types';
import { ConfirmInformationProps, ConfirmInformationQueryParams, ConfirmInformationUseParamsProps } from './ConfirmInformation.types';
import { GoMotoComponent, GoMotoFunction } from '../../../API/GoMoto.types';
import { Dealer } from '../../../context/Kiosk/kiosk.types';

/**
 * This component displays an box of information that aggregates all of the important and relevant information
 * regarding the customer's appointment.
 *
 * @param onInformationCorrect - {@link ConfirmInformationProps.onInformationCorrect onInformationCorrect} description.
 * @param onInformationIncorrect - {@link ConfirmInformationProps.onInformationIncorrect onInformationIncorrect} description.
 */
export const ConfirmInformation: GoMotoComponent<ConfirmInformationProps> = ({
    onInformationCorrect,
    onInformationIncorrect,
}) => {
    const Localization: LocalizationType = useLocalization();
    const {
        key: kioskKey,
        kioskType,
        dealer: { key: dealerKey, primaryLanguage, dmsAppointmentOffsetMinutes: offsetMinutes },
    } = useKioskState();
    const { appointment: blankAppointment } = useEnsureAppointment();
    const params = useParams<ConfirmInformationUseParamsProps>();
    const { search } = useLocation();
    const navigate: NavigateFunction = useNavigate();
    const saveAppointment = useAppointmentUpdateAndPersist();
    const appointmentDispatch = useAppointmentDispatch();
    const maskPhone = useMaskPhone();
    const maskEmail = useMaskEmail();
    const cartDispatch = useCartDispatch();
    const localeDispatch = useLocalizationDispatch();
    const { computeNextAvailableCheckinTime } = useKioskTimeTable();
    const switchDealer = useSwitchDealer();
    const additionalDealers = useAdditionalDealers();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();
    const backgroundDispatch = useBackgroundDataDispatch();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [appointment, setAppointment] = useState<IAppointment | undefined>(undefined);
    const [apptNotFound, setApptNotFound] = useState<boolean>(false);
    const [apptComplete, setApptComplete] = useState<boolean>(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(true);
    const cartPopulatedRef = useRef(false);
    const parsed: ConfirmInformationQueryParams = qs.parse(search, { ignoreQueryPrefix: true });
    const isDmsApptKey: boolean = parsed.isDmsApptKey === 'true';
    const isHereLandingPage: boolean = parsed.isHere === 'true';

    useEffect(() => {
        const loadAppointment: GoMotoFunction<[string], Promise<void>> = async (apptKey) => {
            try {
                let loadedAppointment: IAppointment | null = null;
                if (isDmsApptKey) {
                    const dmsAppointment: IAppointment | null = await getDMSAppointment({
                        kioskKey,
                        dealerKey,
                        dmsAppointmentKey: apptKey,
                    });
                    if (dmsAppointment) {
                        const { key: dmsAppointmentKey, id: dmsAppointmentId } = dmsAppointment;
                        loadedAppointment = {
                            ...dmsAppointment,
                            key: '',
                            dmsAppointmentKey,
                            dmsAppointmentId,
                            locale: primaryLanguage,
                        };
                    }
                }
                else {
                    loadedAppointment = await getAppointment(apptKey);
                    if (loadedAppointment) {
                        localeDispatch({
                            type: LocalizationDispatchTypes.SetLocale,
                            payload: loadedAppointment?.locale,
                        });
                    }
                    if (loadedAppointment && dealerKey !== loadedAppointment.dealerKey) {
                        const newDealer: Dealer | undefined = additionalDealers.find((i) => i.key === loadedAppointment?.dealerKey);
                        newDealer && newDealer.key !== dealerKey && switchDealer(newDealer);
                    }
                }
                if (!loadedAppointment) {
                    setApptNotFound(true);
                }
                else if (
                    loadedAppointment.status === 'completed' ||
                    (loadedAppointment.status === 'scheduled' && (loadedAppointment.keySlotNumber || isDmsApptKey))
                ) {
                    setApptComplete(true);
                }
                else {
                    /**
                     * Block resuming a mobile check-in started over two days ago (web only), since it
                     * would fall outside the Summary Dashboard's two-day window. Measure against `created`
                     * (the same column the dashboard keys off); a missing date is treated as expired.
                     * Gate on `mobileCheckin` rather than `mobileCheckInUrl`: the URL is wiped the first
                     * time a session is resumed (see saveAppointmentData below), so a check-in that was
                     * already resumed once has no URL and would otherwise slip past this limit forever.
                     * `mobileCheckin` stays true for the life of the mobile check-in and is false for a
                     * first-time check-in on an older appointment, which must not be blocked.
                     */
                    if (!isElectron() && loadedAppointment.mobileCheckin && isMobileCheckInResumptionExpired(loadedAppointment.created)) {
                        navigate(AppRoutePaths.ImHere, { replace: true });

                        return;
                    }

                    const appt: IAppointment | undefined = {
                        ...blankAppointment,
                        ...loadedAppointment,
                    };

                    // On mobile web, a resumed completed check-in is a full mobile check-in; unlock the flag-gated completion routes (the kiosk already has them via isElectron).
                    if (!isElectron() && loadedAppointment.mobileCheckInComplete) {
                        appointmentDispatch({
                            type: AppointmentDispatchTypes.AllowFullMobileCheckin,
                            payload: true,
                        });
                    }

                    if (isHereLandingPage || !isElectron()) {
                        if (!cartPopulatedRef.current) {
                            cartPopulatedRef.current = true;

                            loadedAppointment.servicesConfirmed?.forEach((service: ServiceComment) => {
                                const fullService: IService = {
                                    id: service.id,
                                    name: service.name || '',
                                    nameAltLang: service.nameAltLang || '',
                                    description: service.description || '',
                                    descriptionAltLang: service.descriptionAltLang || '',
                                    price: service.price,
                                    priceLow: service.priceLow,
                                    priceHigh: service.priceHigh,
                                    spgPricing: service.spgPricing,
                                    category: ServiceCategory.All,
                                    imagePath: '',
                                    imageUrl: '',
                                    isTransport: service.isTransport,
                                    useSpgPricing: !!service.spgPricing,
                                    comment: service.comment,
                                };

                                cartDispatch({
                                    type: Types.AddService,
                                    payload: fullService,
                                });
                            });

                            if (!!loadedAppointment.xtimeServices) {
                                loadedAppointment.xtimeServices.forEach((service: Pick<IXtimeServiceBase, XTimeServiceProperties>) => {
                                    cartDispatch({
                                        type: Types.AddXtimeService,
                                        payload: {
                                            id: service.id,
                                            goMotoId: service.goMotoId,
                                            type: 'standard',
                                            name: service.name,
                                            opCode: service.opCode,
                                            price: service.price,
                                            isCommonRepair: service.isCommonRepair,
                                            comment: service.comment,
                                            description: service.description,
                                        },
                                    });
                                });
                            }

                            if (!!loadedAppointment.maintenanceMenusConfirmed) {
                                loadedAppointment.maintenanceMenusConfirmed.forEach((maintenanceMenu: MaintenanceMenu) => {
                                    cartDispatch({
                                        type: Types.AddMaintMenu,
                                        payload: maintenanceMenu,
                                    });
                                });
                            }

                            if (loadedAppointment.rndServices) {
                                loadedAppointment.rndServices.forEach((rndService: RNDService) => {
                                    cartDispatch({
                                        type: Types.AddRNDService,
                                        payload: rndService,
                                    });
                                });
                            }

                            if (loadedAppointment.comment) {
                                cartDispatch({
                                    type: Types.AddComment,
                                    payload: {
                                        key: 'Services',
                                        comment: loadedAppointment.comment,
                                    },
                                });
                            }
                        }

                        localeDispatch({
                            type: LocalizationDispatchTypes.SetLocale,
                            payload: loadedAppointment.locale ?? primaryLanguage,
                        });

                        if (loadedAppointment.transportation) {
                            cartDispatch({
                                type: Types.SetTransportation,
                                payload: {
                                    type: loadedAppointment.transportation.type,
                                    subtype: loadedAppointment.transportation.subtype,
                                },
                            });
                        }
                    }

                    /**
                     * If this is the outdoor kiosk and the the user made it past the check-in summary
                     * but hasn't dropped off their key yet, redirect them to the keydrop process.
                     */
                    if (kioskType === 'outdoor' && loadedAppointment.status === 'scheduled' && !loadedAppointment.keySlotNumber) {
                        // TODO: These dispatches should be combined because they trigger two individual re-renders instead of one.
                        appointmentDispatch({
                            type: AppointmentDispatchTypes.AllowFullMobileCheckin,
                            payload: true,
                        });
                        appointmentDispatch({
                            type: AppointmentDispatchTypes.Update,
                            payload: appt,
                        });
                        navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Keydrop, Appointment_KeydropRoutePaths.Locker));

                        return;
                    }

                    if (!appt.contactPreference) {
                        await saveAppointment(appt, true);
                        navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Details), { replace: true });
                    }

                    setAppointment({ ...appt });
                }
                setIsLoading(false);
            }
            catch (error: unknown) {
                console.error(error);
                navigate('/', { replace: true });
            }
        };

        if (!isLoading && params.apptKey && !appointment && !apptNotFound && !apptComplete) {
            setIsLoading(true);
            loadAppointment(params.apptKey);
        }
    }, [
        params.apptKey,
        isLoading,
        navigate,
        appointment,
        blankAppointment,
        isDmsApptKey,
        kioskKey,
        apptNotFound,
        apptComplete,
        cartDispatch,
        isHereLandingPage,
        kioskType,
        appointmentDispatch,
        localeDispatch,
        dealerKey,
        additionalDealers,
        switchDealer,
        primaryLanguage,
        saveAppointment,
    ]);

    const handleOnInformationCorrect: MouseEventHandler<HTMLElement> = async () => {
        await saveAppointmentData();
        if (appointment?.mobileCheckInUrl) {
            backgroundDispatch({
                type: BackgroundDispatchTypes.SetPastMileagePage,
                payload: true,
            });
        }
        onInformationCorrect(appointment?.mobileCheckInUrl);
    };

    const handleOnInformationIncorrect: MouseEventHandler<HTMLElement> = async () => {
        onInformationIncorrect();
    };

    const saveAppointmentData: GoMotoFunction<[void], Promise<void>> = async () => {
        if (appointment) {
            if (appointment.mobileCheckInUrl) {
                // If resuming a session
                await saveAppointment(
                    /**
                     * If we are resuming a session, wipe the stored off url
                     */
                    {
                        ...appointment,
                        mobileCheckInUrl: '',
                    },
                    true,
                    /**
                     * For non-DMS appointments, we would have already pushed the
                     * customer info and our cron task might not have pulled the DMS
                     * key yet, so don't insert duplicate records.
                     */
                    isDmsApptKey,
                );
            }
            else {
                // If is landing from initial check-in email
                await saveAppointment(
                    {
                        ...appointment,
                        appointmentDateTime:
                            // Don't re-schedule the appointment to the next available day if we are past closing time but not past closing time + the offset minutes amount.
                            dayjs()
                                .add(offsetMinutes ?? 0, 'minute')
                                .isAfter(appointment.appointmentDateTime)
                                ? computeNextAvailableCheckinTime()
                                    .toISOString()
                                : appointment.appointmentDateTime,
                    },
                    true,
                    isDmsApptKey,
                );
            }

            if (isDmsApptKey) {
                appointmentDispatch({
                    type: AppointmentDispatchTypes.HasAppointment,
                    payload: true,
                });
            }
        }
    };

    const getDataBox: GoMotoFunction<[void], DataBoxItem[]> = () => {
        if (appointment) {
            const dataBox: DataBoxItem[] = [
                {
                    title: Localization.Phone,
                    value: formatAsPhoneNumber(appointment.phone),
                    masked: maskPhone,
                },
                {
                    title: Localization.Email,
                    value: appointment.email,
                    masked: maskEmail,
                },
                'spacer',
                {
                    title: Localization.Vehicle,
                    value: displayLocalizedVehicle(appointment)
                        .toString(),
                    case: 'capitalize',
                },
            ];

            if (isHereLandingPage) {
                dataBox.push({
                    title: Localization.Mileage,
                    value: `${appointment.mileage ? appointment.mileage : ''}`,
                });
            }

            if (appointment.accountType === AccountType.business) {
                return [
                    {
                        title: Localization.Business,
                        value: textCapitalize(appointment.businessName, true),
                    },
                    ...dataBox,
                ] as DataBoxItem[];
            }
            else {
                return [
                    {
                        title: Localization.FirstName,
                        value: textCapitalize(appointment.firstName),
                    },
                    {
                        title: Localization.LastName,
                        value: textCapitalize(appointment.lastName),
                    },
                    ...dataBox,
                ] as DataBoxItem[];
            }
        }
        else {
            return [];
        }
    };

    if (apptNotFound || apptComplete) {
        const handleErrorClose = () => {
            setErrorDialogOpen(false);
            setTimeout(() => navigate('/', { replace: true }), 250);
        };

        return (
            <GMDialogError
                ariaLabel={apptNotFound ? Localization.AppointmentNotFound : Localization.AppointmentCompleted}
                open={errorDialogOpen}
                title={apptNotFound ? Localization.AppointmentNotFound : Localization.AppointmentCompleted}
                onClose={handleErrorClose}
            />
        );
    }
    else if (isLoading || !appointment) {
        return (
            <Spinner />
        );
    }
    else {
        return (
            <ScrollableBody
                heading={
                    <Heading
                        primaryHeading={
                            isHereLandingPage ? (
                                Localization.formatString(Localization.HiCustomer, <GMAppointmentName appointment={appointment} />)
                            ) : (
                                <GMAppointmentName appointment={appointment} />
                            )
                        }
                        secondaryHeading={isHereLandingPage ? Localization.ImHereConfirm : Localization.ThanksForLettingUsKnowYouAreHere}
                        leftAlign={!isElectron()}
                    />
                }
                proceedButtons={
                    <GMButtonGroup
                        buttons={[
                            (
                                <CTAButton
                                    styleType={'primary'}
                                    onClick={handleOnInformationCorrect}
                                    title={Localization.ClickToConfirmCorrect}
                                    label={Localization.YesItIs}
                                    id={Localization.YesItIs}
                                    ariaLabel={Localization.YesItIs}
                                />
                            ),
                            (
                                <CTAButton
                                    styleType={'secondary'}
                                    onClick={handleOnInformationIncorrect}
                                    title={Localization.ClickToReturnToEntry}
                                    label={Localization.NoItIsNot}
                                    id={Localization.ClickToReturnToEntry}
                                    ariaLabel={Localization.ClickToReturnToEntry}
                                />
                            ),
                        ]}
                    />
                }
            >
                <div>
                    <DataBox dataItems={getDataBox()} />
                </div>
            </ScrollableBody>
        );
    }
};
